import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  faInfoCircle,
  faSync,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import {
  greenColor,
  redColor,
  yellowColor,
  blueColor,
} from "../../../UI/DMCharts";
import SkeletonLoader from "../../../UI/SkeletonLoader";

const SimpleStatCard = ({
  title,
  value,
  loading,
  error,
  onRetry,
  color = "green",
  tooltip,
  onClick,
  style = {},
  summary,
}) => {
  // Card should be in loading state if loading is true OR if we haven't received any data yet
  const isLoading = loading || value === undefined || value === null;

  // Extract count and breakdown from value if it's an object
  const displayValue =
    typeof value === "object" && value !== null
      ? value.count !== undefined
        ? value.count === 2000
          ? "2000+"
          : value.count
        : 0
      : value !== undefined
      ? value === 2000
        ? "2000+"
        : value
      : 0;

  // Extract breakdown information
  const breakdown =
    typeof value === "object" && value?.breakdown ? value.breakdown : null;

  if (isLoading) {
    return (
      <div
        style={{
          cursor: onClick ? "pointer" : "default",
          ...style,
        }}
        onClick={onClick}
      >
        <SkeletonLoader type="card" height="120px" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="chart-col chart-col-33 stat-card"
        style={{
          background: "#fff3f3",
          color: "white",
          textAlign: "center",
          cursor: onClick ? "pointer" : "default",
          border: `2px solid ${redColor}`,
          borderRadius: "8px",
          ...style,
        }}
        onClick={onClick}
      >
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          style={{
            color: redColor,
            fontSize: "25px",
            margin: "10px auto",
            display: "block",
          }}
        />
        <h2
          style={{
            marginTop: "20px",
            color: redColor,
            fontSize: "14px",
            fontWeight: "600",
            marginBottom: "8px",
          }}
        >
          {title}
        </h2>
        <div style={{ color: redColor, margin: "10px 0", fontSize: "12px" }}>
          Error: {typeof error === "string" ? error : "Failed to load"}
        </div>
        {onRetry && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRetry();
            }}
            style={{
              background: redColor,
              color: "white",
              border: "none",
              padding: "8px 16px",
              borderRadius: "4px",
              cursor: "pointer",
              display: "block",
              margin: "10px auto",
              fontSize: "12px",
            }}
          >
            <FontAwesomeIcon icon={faSync} style={{ marginRight: "8px" }} />
            Retry
          </button>
        )}
      </div>
    );
  }

  // Enhanced color handling with glass effects and proper gradients
  const getBackgroundColor = () => {
    switch (color) {
      case "red":
        return `linear-gradient(135deg, ${redColor}dd, ${redColor}bb)`;
      case "yellow":
        return `linear-gradient(135deg, ${yellowColor}dd, ${yellowColor}bb)`;
      case "blue":
        return `linear-gradient(135deg, ${blueColor}dd, ${blueColor}bb)`;
      case "green":
      default:
        return `linear-gradient(135deg, ${greenColor}dd, ${greenColor}bb)`;
    }
  };

  const getBorderColor = () => {
    switch (color) {
      case "red":
        return redColor;
      case "yellow":
        return yellowColor;
      case "blue":
        return blueColor;
      case "green":
      default:
        return greenColor;
    }
  };

  // Render breakdown information
  const renderBreakdown = () => {
    if (!breakdown || Object.keys(breakdown).length === 0) return null;

    const sortedBreakdown = Object.entries(breakdown)
      .sort(([, a], [, b]) => b - a) // Sort by count descending
      .slice(0, 5); // Show top 5

    return (
      <div
        style={{
          marginTop: "8px",
          fontSize: "11px",
          lineHeight: "1.3",
          opacity: "0.9",
          maxHeight: "60px",
          overflow: "hidden",
          position: "relative",
          zIndex: 1,
        }}
      >
        {sortedBreakdown.map(([name, count], index) => (
          <div key={index} style={{ marginBottom: "2px" }}>
            {name}: {count}
          </div>
        ))}
      </div>
    );
  };

  // Render summary text
  const renderSummary = () => {
    if (!summary) return null;

    return (
      <div
        style={{
          marginTop: "6px",
          fontSize: "11px",
          lineHeight: "1.2",
          opacity: "0.85",
          fontStyle: "italic",
          position: "relative",
          zIndex: 1,
        }}
      >
        {summary}
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          .stat-card * {
            text-shadow: none !important;
          }
          
          .stat-card *:not(.stat-card) {
            box-shadow: none !important;
          }
          
          .stat-card .main-number {
            color: white;
            font-weight: bold;
            margin: 0;
            font-size: ${breakdown ? "24px" : "32px"};
          }
          
          /* Large desktop screens */
          @media (min-width: 1200px) {
            .stat-card .main-number {
              font-size: ${breakdown ? "38px" : "52px"};
            }
            .stat-card {
              min-height: ${
                breakdown || summary ? "160px" : "120px"
              } !important;
              padding: 16px 12px !important;
            }
          }
          
          /* Extra large screens */
          @media (min-width: 1600px) {
            .stat-card .main-number {
              font-size: ${breakdown ? "48px" : "68px"};
            }
            .stat-card {
              min-height: ${
                breakdown || summary ? "180px" : "140px"
              } !important;
              padding: 20px 16px !important;
            }
          }
          
          /* Ultra wide screens */
          @media (min-width: 2000px) {
            .stat-card .main-number {
              font-size: ${breakdown ? "56px" : "84px"};
            }
            .stat-card {
              min-height: ${
                breakdown || summary ? "200px" : "160px"
              } !important;
              padding: 24px 20px !important;
            }
          }
        `}
      </style>
      <div
        className="chart-col chart-col-33 stat-card"
        style={{
          cursor: onClick ? "pointer" : "default",
          background: getBackgroundColor(),
          color: "white",
          textAlign: "center",
          border: `2px solid ${getBorderColor()}`,
          borderRadius: "8px",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          minHeight: breakdown || summary ? "140px" : "auto",
          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
          backdropFilter: "blur(10px)",
          position: "relative",
          overflow: "hidden",
          ...style,
        }}
        onClick={onClick}
        onMouseEnter={(e) => {
          if (onClick) {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 8px 25px rgba(0,0,0,0.2)";
          }
        }}
        onMouseLeave={(e) => {
          if (onClick) {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 15px rgba(0, 0, 0, 0.1)";
          }
        }}
      >
        {/* Glass effect overlay */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255, 255, 255, 0.1)",
            pointerEvents: "none",
          }}
        />

        {tooltip && (
          <span data-tip data-for={`tooltip-${title.replace(/\s+/g, "")}`}>
            <FontAwesomeIcon
              style={{
                fontSize: "20px",
                marginBottom: "8px",
                position: "relative",
                zIndex: 1,
              }}
              icon={faInfoCircle}
            />
            <ReactTooltip id={`tooltip-${title.replace(/\s+/g, "")}`}>
              {tooltip}
            </ReactTooltip>
          </span>
        )}
        <h2
          style={{
            marginTop: tooltip ? "8px" : "0",
            color: "white",
            fontSize: "14px",
            fontWeight: "600",
            marginBottom: "8px",
            lineHeight: "1.2",
            position: "relative",
            zIndex: 1,
          }}
        >
          {title}
        </h2>
        <h1 className="main-number" style={{ position: "relative", zIndex: 1 }}>
          {displayValue}
        </h1>
        {renderSummary()}
        {renderBreakdown()}
      </div>
    </>
  );
};

export default SimpleStatCard;
